import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { AccessAuthGuard } from './shared/guards/access-auth-guard.service';
import { DashboardAuthGuard } from './shared/guards/dashboard-auth-guard.service';
import { MetadataAuthGuard } from './shared/guards/metadata-auth-guard.service';
import { AdminAuthGuard } from './shared/guards/admin-auth-guard.service';
import { BussolaAuthGuard } from './shared/guards/bussola-auth-guard.service';
import { UgcReviewAuthGuard } from './shared/guards/ugc-review-auth-guard.service';
import { EventClaimAuthGuard } from './shared/guards/event-claim-auth-guard.service';
import { ContentCreatorAuthGuard } from './shared/guards/content-creator-auth-guard.service';
import { environment } from '../environments/environment';
import { AuthService } from './service/auth/auth.service';
import { E2E_HARNESS_ROUTE } from './service/auth/keycloak.factory';

export const routes: Routes = [
  ...(!environment.IS_PRODUCTION ? [{
    path: E2E_HARNESS_ROUTE,
    loadComponent: () => import('./views/bussola/bussola-content-editor-e2e/bussola-content-editor-e2e.component')
      .then(m => m.BussolaContentEditorE2eComponent)
  }] : []),
  {
    // Not always the dashboard: it needs a role of its own, and a manager without it would be sent to the list with a warning.
    path: '',
    redirectTo: () => inject(AuthService).homeRoute(),
    pathMatch: 'full'
  },
  {
    path: 'editor',
    canActivate: [AccessAuthGuard, ContentCreatorAuthGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./views/xplore-creator/pages/creator/creator.component').then(m => m.CreatorComponent)
      },
      {
        path: 'explore',
        loadComponent: () => import('./views/xplore-creator/pages/forms/form-explore/form-explore.component').then(m => m.FormExploreComponent)
      },
      {
        path: 'explore/:id',
        loadComponent: () => import('./views/xplore-creator/pages/forms/form-explore/form-explore.component').then(m => m.FormExploreComponent)
      },
      {
        path: 'company',
        loadComponent: () => import('./views/xplore-creator/pages/forms/form-company/form-company.component').then(m => m.FormCompanyComponent)
      },
      {
        path: 'company/:id',
        loadComponent: () => import('./views/xplore-creator/pages/forms/form-company/form-company.component').then(m => m.FormCompanyComponent)
      },
      {
        path: 'commercial',
        loadComponent: () => import('./views/xplore-creator/pages/forms/form-commercial/form-commercial.component').then(m => m.FormCommercialComponent)
      },
      {
        path: 'commercial/:id',
        loadComponent: () => import('./views/xplore-creator/pages/forms/form-commercial/form-commercial.component').then(m => m.FormCommercialComponent)
      },
      {
        path: 'event',
        loadComponent: () => import('./views/xplore-creator/pages/forms/form-event/form-event.component').then(m => m.FormEventComponent)
      },
      {
        path: 'event/:id',
        loadComponent: () => import('./views/xplore-creator/pages/forms/form-event/form-event.component').then(m => m.FormEventComponent)
      }
    ]
  },
  {
    path: 'dashboard',
    loadComponent: () => import('./views/dashboard/dashboard/dashboard.component').then(m => m.DashboardComponent),
    canActivate: [AccessAuthGuard, DashboardAuthGuard]
  },
  {
    path: 'list',
    loadComponent: () => import('./views/xplore-list/item-list/item-list.component').then(m => m.ItemListComponent),
    canActivate: [AccessAuthGuard]
  },
  {
    path: 'review',
    children: [
      {
        path: 'ugc',
        loadComponent: () => import('./views/ugc-review/ugc-review-list/ugc-review-list.component').then(m => m.UgcReviewListComponent),
        canActivate: [AccessAuthGuard, UgcReviewAuthGuard]
      },
      {
        path: 'ugc/:submissionId',
        loadComponent: () => import('./views/ugc-review/ugc-review-detail/ugc-review-detail.component').then(m => m.UgcReviewDetailComponent),
        canActivate: [AccessAuthGuard, UgcReviewAuthGuard]
      },
      {
        path: 'claims',
        loadComponent: () => import('./views/event-claims/event-claim-list/event-claim-list.component').then(m => m.EventClaimListComponent),
        canActivate: [AccessAuthGuard, EventClaimAuthGuard]
      },
      {
        path: 'claims/:claimId',
        loadComponent: () => import('./views/event-claims/event-claim-detail/event-claim-detail.component').then(m => m.EventClaimDetailComponent),
        canActivate: [AccessAuthGuard, EventClaimAuthGuard]
      }
    ]
  },
  {
    path: 'login',
    loadComponent: () => import('./views/login/login-page/login-page.component').then(m => m.LoginPageComponent),
    canActivate: [AccessAuthGuard]
  },
  {
    path: 'metadata',
    loadComponent: () => import('./views/metadata/metadata-list/metadata-list.component').then(m => m.MetadataListComponent),
    canActivate: [AccessAuthGuard, MetadataAuthGuard]
  },
  {
    path: 'metadata/territori/:code',
    loadComponent: () => import('./views/metadata/territory-page/territory-page.component').then(m => m.TerritoryPageComponent),
    canActivate: [AccessAuthGuard, MetadataAuthGuard]
  },
  {
    path: 'reservation-list/:id',
    loadComponent: () => import('./views/components/reservation-list/reservation-list.component').then(m => m.ReservationListComponent),
    canActivate: [AccessAuthGuard]
  },
  {
    path: 'load-user',
    loadComponent: () => import('./views/load-user/load-user.component').then(m => m.LoadUserComponent)
  },
  {
    path: 'bussola',
    canActivate: [AccessAuthGuard, BussolaAuthGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./views/bussola/article-list/article-list.component').then(m => m.ArticleListComponent)
      },
      {
        path: 'editor',
        loadComponent: () => import('./views/bussola/article-editor/article-editor.component').then(m => m.ArticleEditorComponent)
      },
      {
        path: 'editor/:id',
        loadComponent: () => import('./views/bussola/article-editor/article-editor.component').then(m => m.ArticleEditorComponent)
      }
    ]
  },
  {
    path: 'users-management/:id',
    loadComponent: () => import('./views/users-management/user-editor/user-editor.component').then(m => m.UserEditorComponent),
    canActivate: [AccessAuthGuard, AdminAuthGuard]
  },
  {
    path: 'users-management',
    loadComponent: () => import('./views/users-management/user-list/user-list.component').then(m => m.UserListComponent),
    canActivate: [AccessAuthGuard, AdminAuthGuard]
  },
  {
    path: 'claude',
    loadComponent: () => import('./views/claude-mcp/claude-mcp.component').then(m => m.ClaudeMcpComponent),
    canActivate: [AccessAuthGuard]
  },
  {
    // Straight to the home page: a redirect to '' ends on '/' with an empty page, Angular does not chain the '' redirect.
    path: '**',
    redirectTo: () => inject(AuthService).homeRoute()
  }
];
